import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, CheckCircle2, Undo2 } from 'lucide-react';
import { coworkForestaApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { LiquidacionTrimestralTable, LT_EGRESO_KEYS } from './LiquidacionTrimestralTable';

const MESES_ABBR = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']; // eslint-disable-line no-unused-vars
const MESES_FULL = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']; // eslint-disable-line no-unused-vars

const EMPTY_FILAS = () => {
  const keys = ['ingresos_membresias', ...LT_EGRESO_KEYS, 'gastos_extraordinarios', 'recupero_expensas'];
  const obj = {};
  keys.forEach((k) => { obj[k] = { m1: '', m2: '', m3: '', obs: '' }; });
  return obj;
};

// Ciclo actual con trimestres FIJOS pedidos por el negocio:
// Mayo-Julio, Agosto-Octubre y Noviembre-Diciembre (este último de 2 meses).
export function buildTrimestres() {
  const y = new Date().getFullYear();
  return [
    { key: `${y}-05`, label: `Mayo - Julio ${y}`, mesesLabels: [`may-${y}`, `jun-${y}`, `jul-${y}`], rendLabel: `agosto ${y}` },
    { key: `${y}-08`, label: `Agosto - Octubre ${y}`, mesesLabels: [`ago-${y}`, `sep-${y}`, `oct-${y}`], rendLabel: `noviembre ${y}` },
    { key: `${y}-11`, label: `Noviembre - Diciembre ${y}`, mesesLabels: [`nov-${y}`, `dic-${y}`, ''], rendLabel: `enero ${y + 1}` },
  ];
}

// Trimestre del ciclo que corresponde al mes actual.
export function currentCycleKey() {
  const y = new Date().getFullYear();
  const m = new Date().getMonth(); // 0-11
  if (m >= 10) return `${y}-11`;   // nov, dic
  if (m >= 7) return `${y}-08`;    // ago-oct
  return `${y}-05`;                // may-jul (default)
}

export default function CoworkLiquidacionTrimestral({ edificioId }) {
  const trimestres = React.useMemo(buildTrimestres, []);
  const [trimestreKey, setTrimestreKey] = useState(currentCycleKey);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filas, setFilas] = useState(EMPTY_FILAS());
  const [mesesLabels, setMesesLabels] = useState(['Mes 1', 'Mes 2', 'Mes 3']);
  const [estado, setEstado] = useState('borrador');
  const [feePct, setFeePct] = useState(8);
  const [ocupacionPct, setOcupacionPct] = useState(0);
  const [savedList, setSavedList] = useState([]);

  // Opciones del selector = trimestres FIJOS del ciclo actual (orden personalizado:
  // Mayo-Julio, Agosto-Octubre, Noviembre-Diciembre) + cualquier trimestre guardado
  // fuera del ciclo (para poder ver/editar históricos), agregados al final.
  const trimestreOptions = React.useMemo(() => {
    const cycleKeys = new Set(trimestres.map((t) => t.key));
    const extras = savedList
      .filter((s) => !cycleKeys.has(s.trimestre_key))
      .sort((a, b) => (a.trimestre_key < b.trimestre_key ? 1 : -1))
      .map((s) => ({ key: s.trimestre_key, label: s.trimestre_label || s.trimestre_key, mesesLabels: s.meses_labels, rendLabel: s.rendicion_label }));
    return [...trimestres, ...extras];
  }, [trimestres, savedList]);

  const selTrim = trimestreOptions.find((t) => t.key === trimestreKey) || trimestres.find((t) => t.key === trimestreKey) || trimestres[0];

  // Cargar lista de trimestres guardados (para exponer históricos fuera del ciclo).
  useEffect(() => {
    if (!edificioId) return;
    coworkForestaApi.listLiquidacionesTrimestrales(edificioId)
      .then((r) => setSavedList(r.data || []))
      .catch(() => {});
  }, [edificioId]);

  const load = useCallback(async () => {
    if (!edificioId || !trimestreKey) return;
    setLoading(true);
    try {
      const res = await coworkForestaApi.getLiquidacionTrimestral(edificioId, trimestreKey);
      const d = res.data;
      const srcFilas = d.filas || {};
      const merged = EMPTY_FILAS();
      Object.keys(merged).forEach((k) => {
        const f = srcFilas[k];
        if (f) merged[k] = { m1: f.m1 ?? '', m2: f.m2 ?? '', m3: f.m3 ?? '', obs: f.obs ?? '' };
      });
      // Si el doc no tenía labels de mes (nuevo), precargar los del trimestre elegido.
      const hasLabels = Array.isArray(d.meses_labels) && d.meses_labels.some((x) => x && x !== 'Mes 1' && x !== 'Mes 2' && x !== 'Mes 3');
      setFilas(merged);
      setMesesLabels(hasLabels ? d.meses_labels : (selTrim?.mesesLabels || ['Mes 1', 'Mes 2', 'Mes 3']));
      setEstado(d.estado || 'borrador');
      setFeePct(d.fee_pct || 8);
      setOcupacionPct(d.ocupacion_pct || 0);
    } catch (err) {
      toast.error('No se pudo cargar la liquidación trimestral');
    } finally {
      setLoading(false);
    }
  }, [edificioId, trimestreKey]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleCell = (key, field, value) => {
    setFilas((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };
  const handleMesLabel = (idx, value) => {
    setMesesLabels((prev) => { const c = [...prev]; c[idx] = value; return c; });
  };

  const buildPayload = () => {
    const outFilas = {};
    Object.entries(filas).forEach(([k, f]) => {
      outFilas[k] = { m1: parseFloat(f.m1) || 0, m2: parseFloat(f.m2) || 0, m3: parseFloat(f.m3) || 0, obs: f.obs || '' };
    });
    return {
      trimestre_key: trimestreKey,
      trimestre_label: selTrim?.label || '',
      rendicion_label: selTrim?.rendLabel || '',
      meses_labels: mesesLabels,
      ocupacion_pct: parseFloat(ocupacionPct) || 0,
      filas: outFilas,
    };
  };

  const refreshSaved = async () => {
    try {
      const r = await coworkForestaApi.listLiquidacionesTrimestrales(edificioId);
      setSavedList(r.data || []);
    } catch { /* noop */ }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await coworkForestaApi.saveLiquidacionTrimestral(edificioId, buildPayload());
      toast.success('Liquidación trimestral guardada');
      await load();
      await refreshSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handlePublicar = async (publicar) => {
    setSaving(true);
    try {
      // Asegurar que exista el documento antes de publicar
      await coworkForestaApi.saveLiquidacionTrimestral(edificioId, buildPayload());
      await coworkForestaApi.publicarLiquidacionTrimestral(edificioId, trimestreKey, publicar);
      toast.success(publicar ? 'Rendición publicada para los inversores' : 'Rendición despublicada');
      await load();
      await refreshSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al publicar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="cowork-liquidacion-trimestral">
      {/* Encabezado: trimestre, rendición, fee, estado */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Trimestre</label>
            <select
              value={trimestreKey}
              onChange={(e) => setTrimestreKey(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              data-testid="lt-trimestre-selector"
            >
              {trimestreOptions.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="text-sm">
            <span className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Rendición</span>
            <span className="font-medium text-slate-800" data-testid="lt-rendicion">{selTrim?.rendLabel}</span>
          </div>
          <div className="text-sm">
            <span className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Fee de gestión GCSAS</span>
            <span className="font-medium text-slate-800" data-testid="lt-fee-pct">{feePct}%</span>
          </div>
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Ocupación</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={ocupacionPct}
                onChange={(e) => setOcupacionPct(e.target.value)}
                className="h-9 w-20 rounded-md border border-slate-200 bg-white px-2 text-sm font-medium text-slate-800 text-right focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                data-testid="lt-ocupacion-input"
              />
              <span className="text-sm text-slate-500">%</span>
            </div>
          </div>
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium ${estado === 'publicada' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
            data-testid="lt-estado"
          >
            {estado === 'publicada' ? 'Publicada' : 'Borrador'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saving || loading} variant="outline" data-testid="lt-guardar-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Guardar
          </Button>
          {estado === 'publicada' ? (
            <Button onClick={() => handlePublicar(false)} disabled={saving || loading} variant="ghost" data-testid="lt-despublicar-btn">
              <Undo2 className="w-4 h-4 mr-1.5" /> Despublicar
            </Button>
          ) : (
            <Button onClick={() => handlePublicar(true)} disabled={saving || loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lt-publicar-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Publicar rendición
            </Button>
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-400">
        Cargá los montos por mes. La columna <b>Trimestre</b> y los totales (Total egresos, Resultado antes de fee, Fee, Resultado neto) se calculan automáticamente. Al publicar, los inversores del pool verán esta tabla en su portal.
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-slate-500" data-testid="lt-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : (
        <LiquidacionTrimestralTable
          filas={filas}
          feePct={feePct}
          mesesLabels={mesesLabels}
          editable
          onCellChange={handleCell}
          onMesLabelChange={handleMesLabel}
        />
      )}
    </div>
  );
}
